import { z } from 'zod';

import { emailSchema } from './auth-schemas.js';
import { normalizeUploadContentType } from './upload-schemas.js';
import { EXTERMINATION_OUTCOMES, PEST_TYPES, VENDOR_TRADES } from './vendor-constants.js';

export const vendorMagicLinkResponse = z.object({
  message: z.string(),
});
export type VendorMagicLinkResponse = z.infer<typeof vendorMagicLinkResponse>;

export const createVendorCompanyInput = z.object({
  name: z.string().trim().min(1).max(200),
  trade: z.enum(VENDOR_TRADES).default('other'),
  primaryContactName: z.string().trim().max(200).nullish().transform((v) => v ?? null),
  primaryContactPhone: z.string().trim().max(40).nullish().transform((v) => v ?? null),
  primaryContactEmail: emailSchema.nullish().transform((v) => v ?? null),
  addressLine1: z.string().trim().max(200).nullish().transform((v) => v ?? null),
  city: z.string().trim().max(100).nullish().transform((v) => v ?? null),
  state: z.string().trim().max(50).nullish().transform((v) => v ?? null),
  postalCode: z.string().trim().max(20).nullish().transform((v) => v ?? null),
  notes: z.string().trim().max(4_000).nullish().transform((v) => v ?? null),
  metadata: z.record(z.unknown()).optional().default({}),
});
export type CreateVendorCompanyInput = z.input<typeof createVendorCompanyInput>;

export const updateVendorCompanyInput = createVendorCompanyInput.partial();
export type UpdateVendorCompanyInput = z.input<typeof updateVendorCompanyInput>;

export const inviteVendorTechnicianInput = z.object({
  name: z.string().trim().min(1).max(120),
  email: emailSchema,
});
export type InviteVendorTechnicianInput = z.infer<typeof inviteVendorTechnicianInput>;

export const vendorSignoffInput = z.object({
  completionNotes: z.string().trim().max(2_000).nullish().transform((v) => v ?? null),
  photoUploadIds: z.array(z.string().uuid()).min(1, 'At least one photo is required'),
  signatureUploadId: z.string().uuid(),
});
export type VendorSignoffInput = z.infer<typeof vendorSignoffInput>;

export const unitLogSignoffInput = z.object({
  outcome: z.enum(EXTERMINATION_OUTCOMES),
  treatmentNotes: z.string().trim().max(2_000).nullish().transform((v) => v ?? null),
  pestTypes: z.array(z.enum(PEST_TYPES)).default([]),
  photoUploadIds: z.array(z.string().uuid()).optional().default([]),
  signatureUploadId: z.string().uuid(),
});
export type UnitLogSignoffInput = z.infer<typeof unitLogSignoffInput>;

export const vendorPresignUploadInput = z.object({
  kind: z.enum(['work_order_photo', 'work_order_signature']),
  contentType: z.string().min(1).max(200).transform(normalizeUploadContentType),
  filename: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[\w.\- ()]+$/, 'Filename contains unsupported characters'),
  sizeBytes: z.number().int().positive().max(10 * 1024 * 1024),
});
